import type { BulletPool } from './bullets';
import type { Player } from './player';

export interface WeaponDef {
  name: string;
  rate: number;
  shots: (player: Player, aimAng: number) => Array<{ ang: number; speed: number; dmg: number; r: number; color: number }>;
}

// 12 tiers — chaque niveau change VISUELLEMENT et mécaniquement.
// Progression : 1 balle → 2 → rapide → gros → 3-voies → laser → triple laser → plasma → quad → anneau → nova → singularité
export const WEAPONS: WeaponDef[] = [
  {
    name: 'PULSATION',
    rate: 0.17,
    shots: (_p, a) => [{ ang: a, speed: 900, dmg: 10, r: 4, color: 0x00ffff }],
  },
  {
    name: 'JUMELLE',
    rate: 0.15,
    shots: (_p, a) => [
      { ang: a - 0.07, speed: 930, dmg: 9, r: 4, color: 0x44ffff },
      { ang: a + 0.07, speed: 930, dmg: 9, r: 4, color: 0x44ffff },
    ],
  },
  {
    name: 'RAPIDE',
    rate: 0.08,
    shots: (_p, a) => [{ ang: a, speed: 1380, dmg: 9, r: 3, color: 0x88ffff }],
  },
  {
    name: 'BLASTEUR',
    rate: 0.27,
    shots: (_p, a) => [{ ang: a, speed: 750, dmg: 30, r: 8, color: 0xffaa00 }],
  },
  {
    name: 'GERBE',
    rate: 0.20,
    shots: (_p, a) => [
      { ang: a - 0.22, speed: 860, dmg: 13, r: 5, color: 0xff8800 },
      { ang: a,        speed: 880, dmg: 14, r: 5, color: 0xffaa00 },
      { ang: a + 0.22, speed: 860, dmg: 13, r: 5, color: 0xff8800 },
    ],
  },
  {
    name: 'LASER',
    rate: 0.10,
    shots: (_p, a) => [{ ang: a, speed: 1700, dmg: 19, r: 3, color: 0xff44ff }],
  },
  {
    name: 'TRIPLE LASER',
    rate: 0.12,
    shots: (_p, a) => [
      { ang: a - 0.09, speed: 1550, dmg: 15, r: 3, color: 0xff66ff },
      { ang: a,        speed: 1700, dmg: 19, r: 4, color: 0xff44ff },
      { ang: a + 0.09, speed: 1550, dmg: 15, r: 3, color: 0xff66ff },
    ],
  },
  {
    name: 'PLASMA',
    rate: 0.30,
    shots: (_p, a) => [{ ang: a, speed: 700, dmg: 50, r: 11, color: 0x00ff88 }],
  },
  {
    name: 'QUAD',
    rate: 0.18,
    shots: (_p, a) => [
      { ang: a - 0.28, speed: 960, dmg: 17, r: 5, color: 0xffdd00 },
      { ang: a - 0.10, speed: 1010, dmg: 20, r: 5, color: 0xffee00 },
      { ang: a + 0.10, speed: 1010, dmg: 20, r: 5, color: 0xffee00 },
      { ang: a + 0.28, speed: 960, dmg: 17, r: 5, color: 0xffdd00 },
    ],
  },
  {
    name: 'ANNEAU',
    rate: 0.55,
    shots: (_p, _a) => Array.from({ length: 12 }, (_, k) => ({
      ang: (k / 12) * Math.PI * 2,
      speed: 830, dmg: 15, r: 5, color: 0x44ddff,
    })),
  },
  {
    name: 'NOVA',
    rate: 0.14,
    shots: (_p, a) => {
      const out: Array<{ ang: number; speed: number; dmg: number; r: number; color: number }> = [];
      for (let k = 0; k < 5; k++) {
        out.push({ ang: a + (k - 2) * 0.20, speed: 1120, dmg: 24, r: 6, color: 0xff88ff });
      }
      out.push({ ang: a, speed: 1950, dmg: 36, r: 5, color: 0xffffff });
      return out;
    },
  },
  {
    name: 'SINGULARITÉ',
    rate: 0.18,
    shots: (_p, a) => {
      const out: Array<{ ang: number; speed: number; dmg: number; r: number; color: number }> =
        Array.from({ length: 12 }, (_, k) => ({
          ang: (k / 12) * Math.PI * 2,
          speed: 920, dmg: 21, r: 5, color: 0xff44ff,
        }));
      out.push({ ang: a, speed: 2100, dmg: 68, r: 12, color: 0xffffff });
      return out;
    },
  },
];

export function fire(
  weaponIdx: number, player: Player, pool: BulletPool, fireTimer: { v: number },
  dmgMul = 1, rateMul = 1,
): boolean {
  const w = WEAPONS[Math.min(weaponIdx, WEAPONS.length - 1)];
  if (fireTimer.v > 0) return false;
  fireTimer.v = w.rate * rateMul;
  const shots = w.shots(player, player.aimAng);
  for (const s of shots) {
    pool.spawn({
      x: player.x + Math.cos(s.ang) * 22,
      y: player.y + Math.sin(s.ang) * 22,
      vx: Math.cos(s.ang) * s.speed,
      vy: Math.sin(s.ang) * s.speed,
      dmg: s.dmg * dmgMul,
      r: s.r,
      color: dmgMul > 1 ? 0xff44ff : s.color,
    });
  }
  return true;
}
